import { useEffect, useState } from "react";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { api, PROPERTY_NAMES } from "@/lib/api";
import { format, addDays } from "date-fns";
import { it } from "date-fns/locale";
import { MoonStar, Loader2, CheckCircle2, BedDouble, User } from "lucide-react";
import { BookingSuccessDialog } from "@/components/BookingSuccessDialog";
import { WhatsAppFloat } from "@/components/WhatsAppFloat";
import { useT } from "@/lib/i18n";

export default function OvernightPage() {
  const { t } = useT();
  const [range, setRange] = useState({ from: new Date(), to: addDays(new Date(), 1) });
  const [propertyFilter, setPropertyFilter] = useState("all");
  const [guests, setGuests] = useState(2);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selection, setSelection] = useState(null);
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    phone: "",
    email: "",
    guests: 2,
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [successBooking, setSuccessBooking] = useState(null);

  const fetchAvailability = async () => {
    if (!range?.from || !range?.to) return;
    if (range.to <= range.from) return;
    setLoading(true);
    try {
      const params = {
        check_in: format(range.from, "yyyy-MM-dd"),
        check_out: format(range.to, "yyyy-MM-dd"),
        guests,
      };
      if (propertyFilter !== "all") params.property = propertyFilter;
      const res = await api.get("/availability/overnight", { params });
      setData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nel caricamento");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAvailability();
    // eslint-disable-next-line
  }, [range?.from, range?.to, propertyFilter, guests]);

  const openBooking = (entry) => {
    setSelection(entry);
    setForm({ first_name: "", last_name: "", phone: "", email: "", guests: guests, notes: "" });
    setDialogOpen(true);
  };

  const submitBooking = async (e) => {
    e.preventDefault();
    if (!selection) return;
    if (!form.first_name.trim() || !form.last_name.trim() || !form.phone.trim()) {
      toast.error("Nome, cognome e telefono sono obbligatori");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        room_id: selection.room.id,
        booking_type: "overnight",
        guest_first_name: form.first_name,
        guest_last_name: form.last_name,
        guest_phone: form.phone,
        guest_email: form.email,
        check_in: format(range.from, "yyyy-MM-dd"),
        check_out: format(range.to, "yyyy-MM-dd"),
        guests: parseInt(form.guests) || 1,
        notes: form.notes,
      };
      const res = await api.post("/bookings", payload);
      setDialogOpen(false);
      setSuccessBooking(res.data);
      fetchAvailability();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore durante la prenotazione");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="overnight-page" className="min-h-screen bg-background">
      <SiteHeader />
      <div className="pt-28 pb-20 max-w-7xl mx-auto px-6 md:px-10">
        <div className="flex items-center gap-3 label-eyebrow mb-3">
          <MoonStar className="w-3 h-3" /> {t("overnight.eyebrow")}
        </div>
        <h1 className="font-display font-light text-5xl md:text-6xl text-primary tracking-tight leading-none">
          {t("overnight.title")}
        </h1>
        <p className="text-foreground/70 mt-5 max-w-2xl text-lg leading-relaxed">
          {t("overnight.desc")}
        </p>

        <div className="grid md:grid-cols-12 gap-8 mt-14">
          <div className="md:col-span-4">
            <div className="card-soft p-6 sticky top-28">
              <div className="label-eyebrow mb-4">Check-in / Check-out</div>
              <Calendar
                mode="range"
                selected={range}
                onSelect={setRange}
                numberOfMonths={1}
                disabled={(d) => d < new Date(new Date().toDateString())}
                locale={it}
                data-testid="overnight-calendar"
              />
              <div className="mt-4 text-sm text-muted-foreground">
                {range?.from && range?.to ? (
                  <>
                    <strong>{format(range.from, "d MMM", { locale: it })}</strong> →{" "}
                    <strong>{format(range.to, "d MMM yyyy", { locale: it })}</strong>
                  </>
                ) : (
                  "Scegli le date"
                )}
              </div>
              <div className="mt-6">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">
                  Ospiti
                </Label>
                <div className="mt-2 grid grid-cols-4 gap-2" data-testid="guest-selector">
                  {[1, 2, 3, 4].map((n) => (
                    <button
                      key={n}
                      onClick={() => setGuests(n)}
                      data-testid={`guest-btn-${n}`}
                      className={`py-2 rounded-lg border text-sm font-medium transition-colors ${
                        guests === n
                          ? "bg-primary text-primary-foreground border-primary"
                          : "bg-white border-border hover:border-primary"
                      }`}
                    >
                      <User className="w-3 h-3 inline mr-1" /> {n}
                    </button>
                  ))}
                </div>
              </div>
              <div className="mt-6">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Struttura</Label>
                <Select value={propertyFilter} onValueChange={setPropertyFilter}>
                  <SelectTrigger className="mt-2" data-testid="overnight-property-filter">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t("common.all_structures")}</SelectItem>
                    <SelectItem value="hotel_due_pini">Hotel Due Pini Conte</SelectItem>
                    <SelectItem value="ga_conte">G&amp;A Conte B&amp;B</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <div className="md:col-span-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-display text-2xl text-primary">
                Camere per {guests} {guests === 1 ? "ospite" : "ospiti"}
                {data?.nights ? ` · ${data.nights} ${data.nights > 1 ? "notti" : "notte"}` : ""}
              </h2>
              {loading && <Loader2 className="w-5 h-5 animate-spin text-primary" />}
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              {data?.rooms
                ?.filter((entry) => entry.available)
                .map((entry) => (
                  <div
                    key={entry.room.id}
                    data-testid={`overnight-room-${entry.room.id}`}
                    className="card-soft p-6 flex flex-col"
                  >
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-11 h-11 rounded-full bg-accent flex items-center justify-center">
                        <BedDouble className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <div className="font-display text-xl text-primary leading-tight">{entry.room.name}</div>
                        <div className="text-[11px] uppercase tracking-widest text-muted-foreground">
                          {PROPERTY_NAMES[entry.room.property]}
                        </div>
                      </div>
                    </div>
                    <div className="text-sm text-muted-foreground mb-4">
                      {entry.room.type === "suite_vasca" ? "Suite con vasca idromassaggio" : "Camera matrimoniale"}
                      <div className="text-[11px] mt-1">
                        Ospiti: {entry.room.min_guests || 1} — {entry.room.max_guests}
                      </div>
                    </div>
                    <div className="mt-auto flex items-end justify-between">
                      <div>
                        <div className="font-display text-2xl text-secondary">
                          €{entry.total_price}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          €{entry.price_per_night}/notte × {entry.nights}
                          {entry.weekend_nights > 0 && (
                            <span className="ml-1 text-amber-600">
                              · +weekend ×{entry.weekend_nights}
                            </span>
                          )}
                        </div>
                      </div>
                      <Button
                        data-testid={`book-overnight-${entry.room.id}`}
                        onClick={() => openBooking(entry)}
                        className="btn-primary pill"
                      >
                        Prenota
                      </Button>
                    </div>
                  </div>
                ))}
              {!loading &&
                (!data?.rooms ||
                  data.rooms.filter((e) => e.available).length === 0) && (
                  <div className="col-span-2 text-center py-16 text-muted-foreground card-soft">
                    Nessuna camera disponibile per {guests}{" "}
                    {guests === 1 ? "ospite" : "ospiti"} in queste date. Prova a cambiare data o numero ospiti.
                  </div>
                )}
            </div>
          </div>
        </div>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md" data-testid="overnight-booking-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Conferma pernottamento</DialogTitle>
            <DialogDescription>
              {selection && data && (
                <>
                  {selection.room.name} · {data.nights} notte{data.nights > 1 ? "i" : ""} · Totale{" "}
                  <strong>€{selection.total_price}</strong>
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitBooking} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Nome *</Label>
                <Input
                  data-testid="input-first-name"
                  value={form.first_name}
                  onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label>Cognome *</Label>
                <Input
                  data-testid="input-last-name"
                  value={form.last_name}
                  onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                  required
                />
              </div>
            </div>
            <div>
              <Label>Numero ospiti</Label>
              <Input
                type="number"
                min={selection?.room?.min_guests || 1}
                max={selection?.room?.max_guests || 2}
                data-testid="input-guests"
                value={form.guests}
                onChange={(e) => setForm({ ...form, guests: e.target.value })}
              />
              {selection?.room && (
                <div className="text-[11px] text-muted-foreground mt-1">
                  Da {selection.room.min_guests || 1} a {selection.room.max_guests} ospiti
                </div>
              )}
            </div>
            <div>
              <Label>Telefono *</Label>
              <Input
                data-testid="input-phone"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                required
              />
            </div>
            <div>
              <Label>Email</Label>
              <Input
                type="email"
                data-testid="input-email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
              />
            </div>
            <div>
              <Label>Note</Label>
              <Input
                data-testid="input-notes"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
              />
            </div>
            <DialogFooter>
              <Button
                type="submit"
                data-testid="submit-overnight-booking"
                disabled={submitting}
                className="btn-primary pill w-full"
              >
                {submitting ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                )}
                Conferma prenotazione
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <BookingSuccessDialog
        open={!!successBooking}
        booking={successBooking}
        onClose={() => setSuccessBooking(null)}
      />

      <SiteFooter />
      <WhatsAppFloat />
    </div>
  );
}
